import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { hasValidProps, logException, ROUTES } from 'Utils';
import * as analytics from 'Utils/analytics';
import ProgressBar from 'Components/ProgressBar';
const { EVENTS } = analytics;

const codeExpiration = 15 * 60000;
const codeLength = 6;
const codeInputs = [...new Array(codeLength).keys()];

const VerificationCode = ({ email, strings, transitionTo, uim }) => {
  const [emailFailed, setEmailFailed] = useState(false);
  const [emailResent, setEmailResent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState([...new Array(codeLength).fill('')]);
  const [codeExpired, setCodeExpired] = useState(false);
  const [codeFailed, setCodeFailed] = useState(false);
  const [valid, setValid] = useState(false);
  const inputContainerEl = useRef();
  const notificationRef = useRef(null);
  const subtextRef = useRef(null);
  const timerRef = useRef(null);

  const setTimer = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setCodeExpired(true);
    }, codeExpiration);
  };

  const clearCodeInput = () => {
    setCode([...new Array(codeLength).fill('')]);
  };

  const focusCodeInput = () => {
    inputContainerEl.current.firstChild.focus();
  };

  const resendEmail = async () => {
    analytics.event({
      action: EVENTS.resendVerification,
      label: 'resendVerification',
    });
    clearCodeInput();
    setEmailFailed(false);
    setEmailResent(true);
    setSubmitting(true);
    setTimeout(() => {
      setEmailResent(false);
    }, 5000);
    try {
      await uim.resendRegistrationVerificationEmail(email);
      setCodeExpired(false);
      setCodeFailed(false);
      setSubmitting(false);
      setTimer();
      focusCodeInput();
    } catch (e) {
      logException(e);
      setEmailFailed(true);
      setSubmitting(false);
      setTimeout(() => {
        setEmailFailed(false);
      }, 5000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const verified = await uim.verifyEmailWithCode(email, code.join(''));
    if (verified) {
      analytics.event({
        action: EVENTS.userVerificationSuccess,
      });
    } else {
      setSubmitting(false);
      setCodeFailed(true);
      clearCodeInput();
      focusCodeInput();
    }
  };

  const handleChange = ({ target }) => {
    const clone = [...code];
    clone[target.id] = target.value;
    setCode(clone);
    target.value.length && target.nextSibling?.select();
  };

  const handleClick = ({ target }) => target.select();

  const handleKeyDown = ({ code, target }) => {
    if (
      (!target.value.length && code === 'Backspace') ||
      code === 'ArrowLeft'
    ) {
      target.previousSibling?.select();
    }
  };

  const onPaste = ({ clipboardData, target }) => {
    const position = parseInt(target.id);
    const pastedText = clipboardData
      .getData('Text')
      ?.slice(0, codeLength - position);
    setCode((code) => code.slice(0, position).concat(pastedText.split('')));
  };

  const renderInputs = () => {
    return codeInputs.map((id) => (
      <input
        autoFocus={!id}
        disabled={submitting}
        id={id}
        inputMode="numeric"
        key={id}
        onChange={handleChange}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        maxLength="1"
        pattern="[0-9]*"
        required
        type="text"
        value={code[id]}
      />
    ));
  };

  useEffect(() => {
    if (!hasValidProps(email)) transitionTo(ROUTES.PAGE_HOME.url);
  }, [email]);

  useEffect(() => {
    setValid(code.filter((char) => /\d/.test(char)).length === codeLength);
  }, [code]);

  useEffect(() => {
    inputContainerEl.current?.addEventListener('paste', onPaste);
    setTimer();
    return () => {
      inputContainerEl.current?.removeEventListener('paste', onPaste);
      clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <>
      {submitting && <ProgressBar />}
      {codeExpired ? (
        <>
          <h1 className="page-heading">{strings.sessionTimeout}</h1>
          <p className="break-word">{strings.verificationCodeTimeout}</p>
          <button onClick={resendEmail} disabled={submitting}>
            {strings.resendVerificationCode}
          </button>
        </>
      ) : (
        <>
          <h1 className="page-heading">{strings.verificationRequired}</h1>
          <p className="break-word">
            {strings.verificationCodeMessage.formatUimTranslation(email)}
          </p>
          <form
            onSubmit={handleSubmit}
            className={codeFailed ? 'input__container--error' : ''}
          >
            <span className="label">
              {codeFailed
                ? strings.verificationCodeInvalid
                : strings.verificationCode}
            </span>
            <div className="input__group" ref={inputContainerEl}>
              {renderInputs()}
            </div>
            <button type="submit" disabled={!valid || submitting}>
              {strings.continue}
            </button>
          </form>
          <SwitchTransition mode="out-in">
            <CSSTransition
              classNames="fade"
              key={
                emailFailed || emailResent
                  ? 'emailTriggered'
                  : 'emailNotTriggered'
              }
              nodeRef={subtextRef}
              timeout={250}
            >
              <div className="subtext" ref={subtextRef}>
                {emailFailed || emailResent ? (
                  <SwitchTransition mode="out-in">
                    <CSSTransition
                      classNames="fade"
                      key={emailFailed ? 'emailFailed' : 'emailResent'}
                      nodeRef={notificationRef}
                      timeout={250}
                    >
                      <div ref={notificationRef}>
                        {emailResent ? (
                          <span className="notification">
                            A new code has been sent!
                          </span>
                        ) : (
                          <span className="error-text">
                            {strings.verificationEmailFailed}
                          </span>
                        )}
                      </div>
                    </CSSTransition>
                  </SwitchTransition>
                ) : (
                  <>
                    <span>{strings.didNotReceive}</span>
                    <span
                      className="link--inline"
                      onClick={resendEmail}
                      disabled={submitting}
                    >
                      {strings.resendEmail}
                    </span>
                  </>
                )}
              </div>
            </CSSTransition>
          </SwitchTransition>
        </>
      )}
    </>
  );
};

VerificationCode.propTypes = {
  email: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  transitionTo: PropTypes.func.isRequired,
  uim: PropTypes.object.isRequired,
};

export default VerificationCode;
